// The theme registry: the single source of truth for which themes exist, their
// display labels, their light/dark mode, and the Monaco editor theme that pairs
// with each. The chrome side of every theme lives in styles/tokens.css under a
// matching [data-theme="<id>"] block; the editor side is defined in
// editor/sealLanguage.ts under the `monaco` name below. The store, settings,
// command palette, title bar, and editor all read from here, so adding a theme
// is: one tokens.css block + one sealLanguage spec + one entry here.
//
// A curated set of well-regarded dark themes, with Tokyo Night as the default.

export type ThemeId =
  | "seal"
  | "tokyo-night"
  | "bitcoin"
  | "catppuccin-mocha"
  | "rose-pine-moon"
  | "night-owl"
  | "dracula";
export type ThemeMode = "dark" | "light";

export interface ThemeDef {
  id: ThemeId;
  label: string;
  mode: ThemeMode;
  /** The Monaco theme name registered for this app theme. */
  monaco: string;
}

export const THEMES: ThemeDef[] = [
  { id: "seal", label: "Seal", mode: "light", monaco: "seal-seal" },
  { id: "tokyo-night", label: "Tokyo Night", mode: "dark", monaco: "seal-tokyo-night" },
  { id: "bitcoin", label: "Bitcoin", mode: "dark", monaco: "seal-bitcoin" },
  { id: "catppuccin-mocha", label: "Catppuccin Mocha", mode: "dark", monaco: "seal-catppuccin-mocha" },
  { id: "rose-pine-moon", label: "Rosé Pine Moon", mode: "dark", monaco: "seal-rose-pine-moon" },
  { id: "night-owl", label: "Night Owl", mode: "dark", monaco: "seal-night-owl" },
  { id: "dracula", label: "Dracula", mode: "dark", monaco: "seal-dracula" },
];

export const DEFAULT_THEME: ThemeId = "seal";
export const THEME_IDS: ThemeId[] = THEMES.map((t) => t.id);

export function themeDef(id: ThemeId): ThemeDef {
  return THEMES.find((t) => t.id === id) ?? THEMES[0];
}

export function monacoThemeFor(id: ThemeId): string {
  return themeDef(id).monaco;
}

/** Coerce an unknown stored value (incl. retired theme ids) to a valid one. */
export function coerceTheme(value: unknown): ThemeId {
  if (typeof value === "string" && (THEME_IDS as string[]).includes(value)) {
    return value as ThemeId;
  }
  return DEFAULT_THEME;
}

/** The next theme in the list, wrapping around (the title-bar quick cycle). */
export function nextTheme(id: ThemeId): ThemeId {
  const i = THEMES.findIndex((t) => t.id === id);
  return THEMES[(i + 1) % THEMES.length].id;
}
